import React, { useState, useCallback, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";

import PatientInformation from "../component/patientInformation";
import PatientDataInformationTabBar from "../component/patientDataInformationTabBar";
import { Constant } from "../../../helper/constant";
import SalesData from "../../sales/page/pharmacy_sales/component/salesData";
import PaginationFooter from "../../../components/footer/paginationFooter";
import ModalDetailPharmacySales from "../../sales/page/pharmacy_sales/component/modalDetailPharmacySales";
import SalesClinicData from "../../sales/page/clinic-sales/component/salesClinicData";
import { formatRupiah } from "../../../helper/formatNumber";
import ModalDetailClinicSales from "../../sales/page/clinic-sales/component/modalDetailClinicSales";

const PatientRecord = () => {
  // =====================
  // Constant Definition
  // =====================
  const navigate = useNavigate();
  const { patientId } = useParams();
  const [transactionData, setTransactionData] = useState([]);
  const [totalPage, setTotalPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState("pharmacy");
  const [page, setPage] = useState(1);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [totalTransaction, setTotalTransaction] = useState(0);
  const [
    isShowDetailTransactionPharmacyModal,
    setIsShowDetailTransactionPharmacyModal,
  ] = useState(false);
  const [
    isShowDetailTransactionClinicModal,
    setIsShowDetailTransactionClinicModal,
  ] = useState(false);
  const [selectedPatientData, setSelectedPatientData] = useState({
    patient_id: "",
    patient_number: "",
    patient_name: "",
    patient_birthday: "",
    patient_gender: "",
    patient_address: "",
    patient_phone_number: "",
  });

  // =====================
  // Function Definition
  // =====================

  const handleTabChange = (value) => {
    setSelectedTab(value);
    resetState();
  };

  const resetState = () => {
    setPage(1);
    setTotalPage(1);
    setTransactionData([]);
  };

  const onNextPageClicked = () => {
    if (page < totalPage) {
      setPage((prevPage) => {
        return prevPage + 1;
      });
    }
  };

  const onPreviousPageClicked = () => {
    if (page !== 1) {
      setPage((prevPage) => {
        return prevPage - 1;
      });
    }
  };

  const onOpenDetailTransaction = (transactionData) => {
    console.log(transactionData);
    setSelectedTransaction(transactionData);
    if (selectedTab === "pharmacy") {
      setIsShowDetailTransactionPharmacyModal(true);
    } else {
      setIsShowDetailTransactionClinicModal(true);
    }
  };

  const onCloseModalDetailTransaction = () => {
    if (selectedTab === "pharmacy") {
      setIsShowDetailTransactionPharmacyModal(false);
    } else {
      setIsShowDetailTransactionClinicModal(false);
    }
    setSelectedTransaction(null);
  };

  // =====================
  // Request Method
  // =====================

  const getPatientData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch(
        Constant.BASE_API_URL + "/pasien/" + patientId,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: "bearer " + localStorage.getItem("token"),
          },
        }
      );
      if (!response.ok) {
        alert("Gagal Menerima Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
      const json = await response.json();
      const data = json.data;
      setSelectedPatientData(data);
    } finally {
      setIsLoading(false);
    }
  }, [navigate, patientId]);

  const getPharmacyTransaction = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams({
        patient_id: patientId,
        page: page,
      }).toString();
      const url = `${Constant.BASE_API_URL}/pasien/pharmacy-transaction?${params}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + localStorage.getItem("token"),
        },
      });

      if (!response.ok) {
        alert("Gagal Menerima Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }

      const json = await response.json();
      const data = json.data;

      if (!data.product_transactions) {
        setTransactionData([]);
      } else {
        setTransactionData(data.product_transactions);
        setTotalTransaction(data.transaction);
      }
      setTotalPage(data.total_pages);
    } catch (error) {
      setTransactionData([]);
    } finally {
      setIsLoading(false);
    }
  }, [navigate, page, patientId]);

  const getClinicTransaction = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams({
        patient_id: patientId,
        page: page,
      }).toString();
      const url = `${Constant.BASE_API_URL}/pasien/clinic-transaction?${params}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + localStorage.getItem("token"),
        },
      });

      if (!response.ok) {
        alert("Gagal Menerima Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }

      const json = await response.json();
      const data = json.data;
      if (!data.medical_transactions) {
        setTransactionData([]);
      } else {
        setTransactionData(data.medical_transactions);
        setTotalTransaction(data.transaction);
      }
      setTotalPage(data.total_pages);
    } catch (error) {
      setTransactionData([]);
    } finally {
      setIsLoading(false);
    }
  }, [navigate, page, patientId]);

  // =====================
  // Use Effect Method
  // =====================

  useEffect(() => {
    getPatientData();
  }, [getPatientData]);

  useEffect(() => {
    if (selectedTab === "pharmacy") {
      getPharmacyTransaction();
    } else {
      getClinicTransaction();
    }
  }, [selectedTab, page, getPharmacyTransaction, getClinicTransaction]);

  return (
    <div style={{ margin: 16 }}>
      <h1>Data Pasien</h1>
      <PatientInformation patient={selectedPatientData} />
      <PatientDataInformationTabBar onSelect={handleTabChange} />
      {isLoading ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginTop: "24px",
          }}
        >
          <div className="loader"></div>
          <p>Sedang Memuat Mohon Menunggu</p>
        </div>
      ) : (
        <div>
          {selectedTab === "pharmacy" ? (
            <SalesData
              transactionData={transactionData}
              onOpenDetailTransaction={onOpenDetailTransaction}
            />
          ) : (
            <SalesClinicData
              transactionData={transactionData}
              onOpenDetailTransaction={onOpenDetailTransaction}
            />
          )}
          <h3>Total Transaksi: {formatRupiah(totalTransaction)}</h3>
          <ModalDetailClinicSales
            isShowModal={isShowDetailTransactionClinicModal}
            clinicTransaction={selectedTransaction}
            onCloseModal={onCloseModalDetailTransaction}
          />
          <ModalDetailPharmacySales
            isShowModal={isShowDetailTransactionPharmacyModal}
            pharmacyTransaction={selectedTransaction}
            onCloseModal={onCloseModalDetailTransaction}
          />
          <PaginationFooter
            isPreviousActive={page > 1}
            isNextActive={page < totalPage}
            onNextClick={onNextPageClicked}
            onPrevClick={onPreviousPageClicked}
          />
        </div>
      )}
    </div>
  );
};

export default PatientRecord;
